import PopoverMenu from "components/PopoverMenu";
import React from "react";
import OrganisationSettings from "./OrganisationSettings";
import OrganisationsList from "../components/OrganisationsList";
import { sidebarMenuStore } from "../SidebarMenuStore";
import { LicenseAppType, OrganisationOUT } from "api-client";
import { organisationStore } from "features/Organisation/OrganisationStore";
import OrganisationDetails from "../components/OrganisationDetails";
import { useNavigate } from "react-router";
import JoinCreateOrganisation from "../components/JoinCreateOrganisation";
import { buildingStore } from "store/Building/BuildingStore";
import { ROUTES_ENUM } from "routes/consts";
import { observer } from "mobx-react-lite";
import clsx from "clsx";
import { IconChevronDown } from "@tabler/icons-react";

interface OrganisationMenuProps {
  buildingsCount?: number;
  isAsset?: boolean;
}

const OrganisationMenu = (props: OrganisationMenuProps) => {
  const { currentOrganisation: defaultOrganisation } = sidebarMenuStore;
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = React.useState(false);
  const menuButtonRef = React.useRef<HTMLButtonElement>(null);

  const closeMenu = () => {
    menuButtonRef.current?.click();
  };

  const handleChangeOrganisation = async (org: OrganisationOUT) => {
    await sidebarMenuStore.onChangeOrganisation(org);
    buildingStore.setBuildingID(undefined);
    buildingStore.setCurrentBuilding(undefined);
    if (org.license?.app === LicenseAppType.Asset) {
      navigate(`/${org.id}/${ROUTES_ENUM.PORTFOLIO_ASSETS}`);
    } else {
      navigate(`/${ROUTES_ENUM.DASHBOARD}`);
    }
    closeMenu();
  };

  function organisationMenuHeader(): React.ReactNode {
    return (
      <div
        className={clsx(
          "flex items-center w-full border-b border-gray-100 p-4 h-[64px] group/menuButton",
          "hover:bg-indigo-50 hover:border-indigo-100 transition cursor-pointer",
          { "bg-indigo-50 border-indigo-100": menuOpen }
        )}
      >
        <OrganisationDetails
          organisation={defaultOrganisation}
          containerClassName="!p-0"
          nameClassName={clsx(
            menuOpen ? "text-indigo-700" : "text-gray-700",
            "!max-w-[230px] group-hover/menuButton:text-indigo-700"
          )}
          countClassName={clsx(
            menuOpen ? "text-indigo-500" : "text-gray-500",
            "group-hover/menuButton:text-indigo-500"
          )}
          buildingsCount={props.buildingsCount}
          logoClassName="!w-8 !h-8"
          isAsset={props.isAsset}
        />
        <IconChevronDown
          stroke={2}
          className={clsx(
            menuOpen ? "text-indigo-700 rotate-180" : "text-gray-500",
            "group-hover/menuButton:text-indigo-700",
            "ml-2 h-6 w-6 transition ease-in-out text-gray-700"
          )}
        />
      </div>
    );
  }

  const otherOrgs = organisationStore.organisations.items
    .filter((org) => org.id !== sidebarMenuStore.currentOrganisation?.id)
    .sort((a, b) => a.name.localeCompare(b.name));

  return (
    <PopoverMenu
      panelClassName="min-w-[300px] !mt-0 !-ml-6"
      ref={menuButtonRef}
      isOpen={(state: boolean) => setMenuOpen(state)}
      customButton={organisationMenuHeader()}
    >
      <OrganisationDetails organisation={defaultOrganisation} />
      <OrganisationSettings />
      <OrganisationsList
        onClick={handleChangeOrganisation}
        otherOrganisations={otherOrgs}
      />
      <JoinCreateOrganisation closeMenu={closeMenu} />
    </PopoverMenu>
  );
};

export default observer(OrganisationMenu);
